<script setup>
// Shared, presentational guardrails panel. API-agnostic: the parent owns
// fetching and persistence. Props in, `change(type, patch)` and `test(type, text)` out.
//
// AGENTS-GUARDRAILS-02: the catalog lists only the types the runtime actually honors
// (`guards/policy.py:TIPOS`). It used to list ten types that the runtime discarded as unknown
// while hiding the three that were protecting every reply — the screen configured nothing that
// ran and displayed nothing that did. The six that need a model come back in phase 3, when
// there is a rail behind them; a switch that turns nothing on is what got this section pulled
// from the menu in the first place.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';

const props = defineProps({
  // Array of guardrail rows: { guardrail_type, enabled, stage, action, block_message, config }
  guardrails: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  // Per-type result of "try it before turning it on":
  // { [type]: { loading, matched, text, term, error } }
  testState: { type: Object, default: () => ({}) },
  // Os rails que NASCEM ligados, vindos do servidor. Desligar um deles é abrir mão de uma
  // proteção que todo agente tem por padrão, e a tela diz isso na hora — não no relatório
  // de incidente. Vazio por omissão: painel usado sem a lista não inventa aviso nenhum.
  railsPadrao: { type: Array, default: () => [] },
});

const emit = defineEmits(['change', 'test']);

const { t } = useI18n();

// Every rail here is deterministic and runs on the output. The "grátis" badge that sat next to
// each one left on 23/09/2026 (Murilo): every rail is free, so it said nothing, and a price tag
// on a safety rail reads like an upsell. If a rail that costs a model call arrives, the badge
// comes back for that rail only.
const GUARDRAIL_META = {
  pii_output: { icon: 'i-lucide-shield-check', stage: 'output' },
  secrets: { icon: 'i-lucide-key', stage: 'output' },
  banned_terms: { icon: 'i-lucide-eye-off', stage: 'output' },
  competitor: { icon: 'i-lucide-building-2', stage: 'output' },
  h9_artifacts: { icon: 'i-lucide-link-2-off', stage: 'output' },
  action_claim: { icon: 'i-lucide-badge-alert', stage: 'output' },
  empty_output: { icon: 'i-lucide-message-square-dashed', stage: 'output' },
};

// Which patterns `pii_output` removes when the customer has not chosen. MIRRORS
// `PADROES_PII_PADRAO` in poweragents/src/poweragents/guards/output.py — the runtime treats a
// missing key as "use the default", so the screen has to show the same thing the runtime would
// do. There is a seam assertion in exercicios/aceite_dos_rails.sh comparing the two: if they
// drift, the screen shows one default and the runtime applies another, silently.
//
// RG and phone are off because neither can be told apart from an ordinary business identifier:
// RG has no national check digit, and a Brazilian mobile is the same 11-digit string as an
// order code. Measured against Le Creuset. The customer turns them on knowing the cost.
const PII_PADRAO = {
  cpf: true,
  cnpj: true,
  cartao: true,
  email: true,
  telefone: false,
  rg: false,
};
const PII_ORDEM = ['cpf', 'cnpj', 'cartao', 'email', 'telefone', 'rg'];
const COM_LISTA = ['banned_terms', 'competitor'];

const expanded = ref(null);
const rascunho = ref({});

function rowFor(type) {
  return (
    props.guardrails.find(g => g.guardrail_type === type) || {
      guardrail_type: type,
      enabled: false,
      stage: GUARDRAIL_META[type].stage,
      action: 'block',
      block_message: '',
      config: {},
    }
  );
}

function configOf(type) {
  return rowFor(type).config || {};
}

const enabledCount = computed(
  () => props.guardrails.filter(g => g.enabled).length
);

// Every change carries the full current state: the upsert used to replace the whole row, so a
// partial patch could silently reset a sibling field.
function emitPatch(type, patch) {
  const row = rowFor(type);
  emit('change', type, {
    enabled: row.enabled,
    stage: GUARDRAIL_META[type].stage,
    action: row.action,
    block_message: row.block_message || '',
    config: row.config || {},
    ...patch,
  });
}

function toggle(type, checked) {
  emitPatch(type, { enabled: checked });
}

// Aviso, e não bloqueio: quem opera pode ter uma razão para desligar, e um rail que não se
// desliga vira agente que ninguém consegue consertar às onze da noite. O que não pode é
// desligar sem saber o que se perde.
function padraoDesligado(type) {
  return props.railsPadrao.includes(type) && !rowFor(type).enabled;
}

function setAction(type, action) {
  emitPatch(type, { action });
}

function setMessage(type, blockMessage) {
  emitPatch(type, { block_message: blockMessage });
}

function setConfig(type, config) {
  emitPatch(type, { config });
}

function termsOf(type) {
  return configOf(type).termos || [];
}

function setTerms(type, termos) {
  setConfig(type, { ...configOf(type), termos });
}

function quotesAllowed(type) {
  return Boolean(configOf(type).allow_in_quotes);
}

function setQuotesAllowed(type, value) {
  setConfig(type, { ...configOf(type), allow_in_quotes: value });
}

function piiOn(type, padrao) {
  const config = configOf(type);
  return padrao in config ? Boolean(config[padrao]) : PII_PADRAO[padrao];
}

// Writes the whole set explicitly, not just the toggled key: once the customer touches this,
// what the screen shows and what the runtime does must stop depending on a default that lives
// in two places.
function setPii(type, padrao, value) {
  setConfig(type, { ...PII_PADRAO, ...configOf(type), [padrao]: value });
}

function runTest(type) {
  const texto = (rascunho.value[type] || '').trim();
  if (texto) emit('test', type, texto);
}

function stateOf(type) {
  return props.testState[type] || {};
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <div
      v-if="!loading && guardrails.length && enabledCount === 0"
      class="flex items-center gap-2.5 px-4 py-3 rounded-lg border border-n-amber-6 bg-n-amber-2 text-sm text-n-amber-11"
    >
      <i class="i-lucide-triangle-alert shrink-0" />
      <span>{{ t('POWER_AGENTS.GUARDRAILS.ALL_OFF') }}</span>
    </div>

    <div v-if="loading" class="py-6 text-xs text-center text-n-slate-10">
      {{ t('POWER_AGENTS.GUARDRAILS.LOADING') }}
    </div>

    <div v-else class="flex flex-col gap-3">
      <div
        v-for="(meta, type) in GUARDRAIL_META"
        :key="type"
        class="transition-colors border rounded-lg"
        :class="
          rowFor(type).enabled
            ? 'border-n-brand/40 bg-n-alpha-1'
            : 'border-n-weak bg-n-background'
        "
      >
        <div class="flex items-center gap-3 px-4 py-3">
          <i
            class="text-sm shrink-0"
            :class="[
              meta.icon,
              rowFor(type).enabled ? 'text-n-brand' : 'text-n-slate-9',
            ]"
          />
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12">
              {{ t(`POWER_AGENTS.GUARDRAILS.TYPES.${type}.LABEL`) }}
            </p>
            <p class="text-xs truncate text-n-slate-10">
              {{ t(`POWER_AGENTS.GUARDRAILS.TYPES.${type}.DESC`) }}
            </p>
          </div>
          <Switch
            :model-value="rowFor(type).enabled"
            class="shrink-0"
            @update:model-value="checked => toggle(type, checked)"
          />
        </div>

        <p
          v-if="padraoDesligado(type)"
          class="px-4 pb-3 text-xs text-n-amber-11"
        >
          {{ t('POWER_AGENTS.GUARDRAILS.PADRAO_DESLIGADO') }}
        </p>

        <div
          v-if="rowFor(type).enabled"
          class="px-4 pb-3 border-t border-n-weak"
        >
          <button
            class="w-full py-1.5 text-[11px] text-left text-n-slate-10 hover:text-n-slate-12"
            @click="expanded = expanded === type ? null : type"
          >
            {{
              expanded === type
                ? t('POWER_AGENTS.GUARDRAILS.COLLAPSE')
                : t('POWER_AGENTS.GUARDRAILS.CONFIGURE')
            }}
          </button>

          <div v-if="expanded === type" class="flex flex-col gap-3 pt-1">
            <p class="text-[11px] leading-relaxed text-n-slate-10">
              {{ t(`POWER_AGENTS.GUARDRAILS.TYPES.${type}.EXAMPLE`) }}
            </p>

            <div class="flex items-center gap-3">
              <label class="w-20 text-xs font-medium shrink-0 text-n-slate-11">
                {{ t('POWER_AGENTS.GUARDRAILS.ACTION') }}
              </label>
              <select
                :value="rowFor(type).action"
                class="px-2.5 py-1.5 text-xs border rounded-md outline-none border-n-weak bg-n-background"
                @change="setAction(type, $event.target.value)"
              >
                <option value="block">
                  {{ t('POWER_AGENTS.GUARDRAILS.ACTION_BLOCK') }}
                </option>
                <option value="warn">
                  {{ t('POWER_AGENTS.GUARDRAILS.ACTION_WARN') }}
                </option>
                <option value="log_only">
                  {{ t('POWER_AGENTS.GUARDRAILS.ACTION_LOG') }}
                </option>
              </select>
            </div>

            <div v-if="COM_LISTA.includes(type)" class="flex flex-col gap-1.5">
              <label class="text-xs font-medium text-n-slate-11">
                {{ t('POWER_AGENTS.GUARDRAILS.TERMS_LABEL') }}
              </label>
              <TagInput
                :model-value="termsOf(type)"
                allow-create
                :auto-open-dropdown="false"
                :placeholder="t('POWER_AGENTS.GUARDRAILS.TERMS_PLACEHOLDER')"
                @update:model-value="value => setTerms(type, value)"
              />
              <p class="text-[11px] text-n-slate-10">
                {{ t('POWER_AGENTS.GUARDRAILS.TERMS_HINT') }}
              </p>
            </div>

            <div v-if="type === 'competitor'" class="flex items-start gap-3">
              <Switch
                :model-value="quotesAllowed(type)"
                class="mt-0.5 shrink-0"
                @update:model-value="value => setQuotesAllowed(type, value)"
              />
              <div class="min-w-0">
                <p class="text-xs font-medium text-n-slate-11">
                  {{ t('POWER_AGENTS.GUARDRAILS.ALLOW_IN_QUOTES') }}
                </p>
                <p class="text-[11px] text-n-slate-10">
                  {{ t('POWER_AGENTS.GUARDRAILS.ALLOW_IN_QUOTES_HINT') }}
                </p>
              </div>
            </div>

            <div v-if="type === 'pii_output'" class="flex flex-col gap-1.5">
              <label class="text-xs font-medium text-n-slate-11">
                {{ t('POWER_AGENTS.GUARDRAILS.PII_PATTERNS') }}
              </label>
              <div class="flex flex-wrap gap-x-5 gap-y-2">
                <div
                  v-for="padrao in PII_ORDEM"
                  :key="padrao"
                  class="flex items-center gap-2"
                >
                  <Switch
                    :model-value="piiOn(type, padrao)"
                    class="shrink-0"
                    @update:model-value="value => setPii(type, padrao, value)"
                  />
                  <span class="text-xs text-n-slate-11">
                    {{
                      t(`POWER_AGENTS.GUARDRAILS.PII_${padrao.toUpperCase()}`)
                    }}
                  </span>
                </div>
              </div>
              <p class="text-[11px] text-n-slate-10">
                {{ t('POWER_AGENTS.GUARDRAILS.PII_SEM_VERIFICACAO_HINT') }}
              </p>
            </div>

            <p
              v-if="!COM_LISTA.includes(type) && type !== 'pii_output'"
              class="text-[11px] text-n-slate-10"
            >
              {{ t('POWER_AGENTS.GUARDRAILS.CONFIG_NONE') }}
            </p>

            <div
              v-if="rowFor(type).action !== 'log_only'"
              class="flex items-start gap-3"
            >
              <label
                class="w-20 pt-1.5 text-xs font-medium shrink-0 text-n-slate-11"
              >
                {{ t('POWER_AGENTS.GUARDRAILS.BLOCK_MESSAGE') }}
              </label>
              <input
                :value="rowFor(type).block_message || ''"
                :placeholder="t('POWER_AGENTS.GUARDRAILS.BLOCK_PLACEHOLDER')"
                class="flex-1 px-2.5 py-1.5 text-xs border rounded-md outline-none reset-base border-n-weak bg-n-background"
                @blur="setMessage(type, $event.target.value)"
              />
            </div>

            <div class="flex flex-col gap-1.5 pt-1 border-t border-n-weak">
              <label class="pt-2 text-xs font-medium text-n-slate-11">
                {{ t('POWER_AGENTS.GUARDRAILS.TEST_TITLE') }}
              </label>
              <textarea
                v-model="rascunho[type]"
                rows="2"
                :placeholder="t('POWER_AGENTS.GUARDRAILS.TEST_PLACEHOLDER')"
                class="px-2.5 py-1.5 text-xs border rounded-md outline-none resize-y reset-base border-n-weak bg-n-background"
              />
              <div class="flex items-center gap-2">
                <button
                  class="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-md border-n-weak text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50"
                  :disabled="
                    stateOf(type).loading || !(rascunho[type] || '').trim()
                  "
                  @click="runTest(type)"
                >
                  <i class="i-lucide-play" />
                  {{ t('POWER_AGENTS.GUARDRAILS.TEST_RUN') }}
                </button>
                <span
                  v-if="stateOf(type).error"
                  class="text-[11px] text-n-ruby-11"
                >
                  {{ t('POWER_AGENTS.GUARDRAILS.TEST_FAILED') }}
                </span>
                <span
                  v-else-if="stateOf(type).matched === true"
                  class="text-[11px] text-n-amber-11"
                >
                  {{ t('POWER_AGENTS.GUARDRAILS.TEST_MATCHED') }}
                  <span v-if="stateOf(type).term" class="font-medium">
                    {{ stateOf(type).term }}
                  </span>
                </span>
                <span
                  v-else-if="stateOf(type).matched === false"
                  class="text-[11px] text-n-teal-11"
                >
                  {{ t('POWER_AGENTS.GUARDRAILS.TEST_CLEAN') }}
                </span>
              </div>
              <p
                v-if="stateOf(type).matched && stateOf(type).text"
                class="px-2.5 py-1.5 text-[11px] rounded-md bg-n-alpha-1 text-n-slate-11 break-words"
              >
                {{ stateOf(type).text }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
